<script setup>
import { WdsButtonLink, WdsEventCard } from '@wds/components';

const props = defineProps({
  list: {
    type: Array,
    required: true,
  },
});
// ### CONSTANTS
const MAX_NUM_VISIBLE_CARDS = 11;
const MAX_VERTICAL_CARDS = 7;

// ### COMPOSABLES
const {
  locale,
  t,
} = useI18n();
const { useUrl } = useUrls();
const { useFormatter } = useFormatters();

// ### DATA
const expandList = ref(false);

// ### COMPUTED
const eventList = computed(() => {
  if (!Array.isArray(props?.list)) {
    return [];
  }

  const visibleEventList = expandList.value ? props.list : props.list?.slice(0, MAX_NUM_VISIBLE_CARDS);

  return visibleEventList?.map((event, idx) => {
    const isVerticalCard = idx < MAX_VERTICAL_CARDS;
    const usesDetailsModel = Boolean(event?.picture && typeof event.picture === 'object' && 'mobile' in event.picture);
    const eventImages = event?.images || [];

    // EVENT PICTURE (FORMAT: MOBILE IMAGE FOR HORIZONTAL CARDS AND DESKTOP IMAGE FOR VERTICAL CARDS)
    let eventPicture = usesDetailsModel
      ? (isVerticalCard ? event.picture?.desktop : event.picture?.mobile) || ''
      : (isVerticalCard ? eventImages[0]?.desktop : eventImages[0]?.mobile) || '';
    eventPicture = eventPicture || '';

    // EVENT LOCATION (FORMAT: CITY, COUNTRY)
    const eventLocation = usesDetailsModel
      ? event.locationName || ''
      : [event?.location?.cityName, event?.location?.countryName].filter(Boolean).join(', ');

    const eventId = event.eventId ?? event.id;

    // EVENT LINK (FORMAT: EVENT ID AND EVENT NAME SLUG)
    const eventLink = useUrl.getInternalEventPageUrl({
      rootSlug: t('t_OGEVENTSSLUG'),
      eventId,
      eventNameSlug: useFormatter.normalizeUrl(event.name),
    });

    const startDate = usesDetailsModel ? event.dates?.startDate : event.startDate;
    const endDate = usesDetailsModel ? event.dates?.endDate : event.endDate;

    const eventCategory = event.eventType?.replace('_', ' ') || '';

    return {
      id: eventId,
      bind: {
        language: locale.value,
        orientation: isVerticalCard ? 'vertical' : 'horizontal',
        name: event.name,
        href: eventLink,
        startDate,
        endDate,
        category: eventCategory[0]?.toUpperCase() + eventCategory?.slice(1),
        picture: eventPicture,
        location: eventLocation,
      },
    };
  });
});

// ### METHODS
function toggleExpandedList() {
  expandList.value = !expandList.value;
}
</script>

<template>
  <div
    v-if="eventList.length > 0"
    class="events-list-container"
  >
    <div class="events-list">
      <WdsEventCard
        v-for="event in eventList"
        :key="event.id"
        v-bind="event.bind"
      />
    </div>
    <WdsButtonLink
      v-if="list.length > MAX_NUM_VISIBLE_CARDS"
      class="events-list-show-more"
      :text="$t(expandList ? 't_SHOWLESS' : 't_SHOWMORE')"
      :icon-end="expandList ? 'chevron-up' : 'chevron-down'"
      @click="toggleExpandedList()"
    />
  </div>
</template>

<style scoped lang="scss">
.events-list-container {
  .events-list {
    display: flex;
    gap: $wds-spacing-m;
    flex-direction: column;

    & > * {
      min-width: wds-rem(288px);
    }

    @include tablet {
      display: grid;
      grid-template-columns: repeat(2, 1fr);

      & > *:nth-child(1),
      & > *:nth-child(2),
      & > *:nth-child(3) {
        grid-column: span 2;
      }
    }

    @include desktop {
      grid-template-columns: repeat(6, 1fr);

      & > *:nth-child(1),
      & > *:nth-child(2),
      & > *:nth-child(3) {
        grid-column: span 2;
      }

      & > *:nth-child(n + 4) {
        grid-column: span 3;
      }
    }

    @include desktop-large {
      grid-template-columns: repeat(12, 1fr);

      & > *:nth-child(1),
      & > *:nth-child(2),
      & > *:nth-child(3) {
        grid-column: span 4;
      }

      & > *:nth-child(n + 4) {
        grid-column: span 3;
      }
    }
  }

  .events-list-show-more {
    margin-top: $wds-spacing-m;
    margin-left: auto;
  }
}
</style>
